import { FC, useCallback, useMemo, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import { BottomPopup } from '@shared/components/BottomPopup';
import { Icon } from '@shared/components/icons';
import Modal from '@shared/components/Modal';
import { ISelectOption, ISelectProps } from '@shared/interfaces/select.interface';
import {
  BankEnum,
  bankOnlyList,
  FinancialInstitutionType,
  securitiesList,
} from '@shared/miscValues';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { getBankLogo, hasBankLogo } from '@shared/utils/getBankLogo';
import { matchKoreanSearch } from '@shared/utils/koreanSearch';
import { Controller } from 'react-hook-form';
import { useId } from 'react-id-generator';
import { useMediaQuery } from 'react-responsive';

/**
 * @param placeholder
 * @param optionList select의 option들.
 * @param control from react-hook-form
 * @param name from react-hook-form
 * @returns Select for desktop
 */

interface IBankSelectOption extends ISelectOption {
  value: BankEnum;
}

type Props = Omit<ISelectProps, 'optionList'>;

const getBankList = (type: FinancialInstitutionType | 'all'): IBankSelectOption[] => {
  let list: BankEnum[];
  if (type === FinancialInstitutionType.Bank) {
    list = bankOnlyList;
  } else if (type === FinancialInstitutionType.Securities) {
    list = securitiesList;
  } else {
    list = [...bankOnlyList, ...securitiesList];
  }
  return list.map((bank) => ({
    label: bank,
    value: bank,
  }));
};

// 로고 없음 플레이스홀더 컴포넌트
const NoLogoPlaceholder: FC<{ name: string }> = ({ name }) => (
  <div className="flex aspect-square w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-500">
    {name.charAt(0)}
  </div>
);

const BankSelect: FC<Props> = ({
  placeholder,
  control,
  name,
  disabled,
  defaultValue = '',
}: Props) => {
  const [activeTab, setActiveTab] = useState<FinancialInstitutionType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const isMobile = !useMediaQuery({
    query: '(min-width: 768px)',
  });
  const { ChevronDown, MagnifyingGlassRegular } = Icon();
  const [id] = useId(1, 'bankSelect');
  const openId = `bankSelect-${id}`;
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  // 필터링된 옵션 목록
  const filteredOptions = useMemo(() => {
    const baseOptions = getBankList(activeTab);
    if (!searchQuery) return baseOptions;
    return baseOptions.filter((option) => matchKoreanSearch(option.label, searchQuery));
  }, [activeTab, searchQuery]);

  // 전체 옵션 (선택된 값 표시용)
  const allOptions = useMemo(() => getBankList('all'), []);

  const handleOpenModal = useCallback(() => {
    setSearchQuery('');
    setOpen(true);
  }, [setOpen]);

  const modalContents = useCallback(
    (value: any, onChange: (...event: any[]) => void, containerClassName = '') => {
      const onConfirm = (option: ISelectOption, onChange: (...event: any[]) => void) => {
        onChange(option.value);
        setOpen(false);
      };

      const tabs = [
        { key: 'all' as const, label: '전체' },
        { key: FinancialInstitutionType.Bank, label: '은행' },
        { key: FinancialInstitutionType.Securities, label: '증권사' },
      ];

      return (
        <div
          className={`space-y-3 p-6 text-sm theme-text-1 theme-bg-1 ${containerClassName}`}
        >
          {/* Title */}
          <h4 className="text-center font-bold">{placeholder}</h4>

          {/* Search Bar */}
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <MagnifyingGlassRegular className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="은행/증권사 검색 (초성 검색 가능)"
              className="w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm theme-bg-1 theme-border-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Item List */}
          <ul className="grid h-[500px] max-h-[calc(100vh-280px)] grid-cols-2 content-start overflow-y-auto pt-3 pb-4">
            {filteredOptions.length === 0 ? (
              <li className="col-span-2 py-4 text-center text-gray-500">
                검색 결과가 없습니다
              </li>
            ) : (
              filteredOptions.map((option, index) => (
                <li
                  key={`${index}-${option.value}-${option.label}`}
                  onClick={() => onConfirm(option, onChange)}
                  className={`flex cursor-pointer items-center justify-start gap-2 px-4 py-2.5 theme-border-1 ${
                    option.value === value
                      ? 'text-white theme-bg-main'
                      : 'hover:theme-bg-5'
                  }`}
                >
                  {hasBankLogo(option.value) ? (
                    <img
                      src={getBankLogo(option.value)}
                      alt="Bank Logo"
                      className="aspect-square w-8"
                    />
                  ) : (
                    <NoLogoPlaceholder name={option.label} />
                  )}
                  <p className="font-medium leading-5">{option.label}</p>
                </li>
              ))
            )}
          </ul>
        </div>
      );
    },
    [
      filteredOptions,
      placeholder,
      setOpen,
      searchQuery,
      activeTab,
      MagnifyingGlassRegular,
    ],
  );

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={(
        { field: { onChange, value, onBlur, ref } }, // value: bankEnum;
      ) => (
        <>
          <div
            className={`select relative flex cursor-pointer items-center justify-between pr-1 text-sm font-bold theme-border-1 ${
              disabled ? 'bg-gray-100 text-gray-700' : 'theme-bg-1'
            }`}
            onClick={() => {
              if (!disabled) {
                const activeElement = document.activeElement as HTMLElement;
                if (activeElement) {
                  activeElement.blur();
                }
                handleOpenModal();
              }
            }}
            ref={ref}
            onBlur={onBlur}
          >
            {value === '' ? (
              <p className="theme-text-6">{placeholder}</p>
            ) : (
              <p>{allOptions.find((option) => option.value === value)?.label}</p>
            )}
            <div>
              <ChevronDown />
            </div>
          </div>
          {isMobile ? (
            <BottomPopup openId={openId}>
              {modalContents(value, onChange, 'w-screen rounded-t-2xl')}
            </BottomPopup>
          ) : (
            <Modal openId={openId}>{modalContents(value, onChange)}</Modal>
          )}
        </>
      )}
    />
  );
};

export default BankSelect;
